import React, { useState, useEffect, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  OutlinedInput,
  ListItemText,
  Chip,
  Typography,
  Grid,
  Paper
} from '@mui/material';

// Color scheme for different series (matching ForecastChartApex)
const COLORS = {
  actual: '#000000',
  forecast: ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'],
  bounds: [
    ['#1976d2', '#42a5f5'],
    ['#2e7d32', '#66bb6a'],
    ['#f57c00', '#ffa726'],
    ['#d32f2f', '#ef5350'],
    ['#7b1fa2', '#ba68c8'],
    ['#00695c', '#4db6ac']
  ]
};

/**
 * Reusable Time Series Chart Component
 * 
 * @param {Object} props
 * @param {Array} props.timeSeriesData - Array of time series data points
 * @param {string} props.forecastStartDate - Forecast start date (ISO string or Date object)
 * @param {string} props.title - Chart title
 * @param {string} props.subtitle - Chart subtitle
 * @param {Object} props.groupingConfig - Configuration for grouping data
 * @param {string} props.groupingConfig.groupBy - Field to group by (e.g., 'forecast_id', 'node_id')
 * @param {Array} props.groupingConfig.seriesTypes - Array of series types to include
 * @param {Object} props.seriesConfig - Configuration for series types
 * @param {Object} props.seriesConfig.aggregated - Configuration for aggregated series
 * @param {Object} props.seriesConfig.external - Configuration for external series
 * @param {number} props.initialWindowWeeks - Initial window in weeks (default: 8)
 * @param {boolean} props.showControls - Whether to show control panel (default: true)
 * @param {boolean} props.showLegend - Whether to show legend (default: true)
 * @param {string} props.height - Chart height (default: '500px')
 */
const TimeSeriesChart = ({
  timeSeriesData = [],
  forecastStartDate,
  title = 'Time Series Chart',
  subtitle = '',
  groupingConfig = {
    groupBy: 'forecast_id',
    seriesTypes: ['aggregated', 'external']
  },
  seriesConfig = {
    aggregated: {
      mean: 'organic_p50',
      lower: 'organic_p10',
      upper: 'organic_p90',
      label: 'Aggregated'
    },
    external: {
      mean: 'external_p50',
      lower: 'external_p10',
      upper: 'external_p90',
      label: 'External'
    }
  },
  initialWindowWeeks = 8,
  showControls = true,
  showLegend = true,
  height = '500px'
}) => {
  // State for controls
  const [visibleSeriesTypes, setVisibleSeriesTypes] = useState(
    groupingConfig.seriesTypes.reduce((acc, type) => {
      acc[type] = true;
      return acc;
    }, {})
  );
  const [visibleBounds, setVisibleBounds] = useState({
    lower: true,
    upper: true
  });
  const [visibleGroups, setVisibleGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Extract unique groups from data
  const availableGroups = useMemo(() => {
    const groups = new Set();
    timeSeriesData.forEach(point => {
      if (point[groupingConfig.groupBy]) {
        groups.add(point[groupingConfig.groupBy]);
      }
    });
    return Array.from(groups).sort();
  }, [timeSeriesData, groupingConfig.groupBy]);

  // Initialize selected groups when data changes
  useEffect(() => {
    if (availableGroups.length > 0 && selectedGroups.length === 0) {
      setSelectedGroups(availableGroups.slice(0, 3)); // Show first 3 groups by default
    }
  }, [availableGroups, selectedGroups.length]);

  // Process data for chart
  const chartData = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return { series: [], categories: [], initialStartDate: null, initialEndDate: null };
    }

    // Parse forecast start date
    let parsedForecastStartDate = null;
    if (forecastStartDate) {
      parsedForecastStartDate = new Date(forecastStartDate);
      // Round to start of day for cleaner date comparison
      parsedForecastStartDate = new Date(
        parsedForecastStartDate.getFullYear(),
        parsedForecastStartDate.getMonth(),
        parsedForecastStartDate.getDate()
      );
    }

    // Calculate initial date range
    let initialStartDate = new Date();
    let initialEndDate = new Date();
    
    if (parsedForecastStartDate) {
      const halfWindow = (initialWindowWeeks * 7) / 2; // Convert weeks to days
      initialStartDate = new Date(parsedForecastStartDate.getTime() - (halfWindow * 24 * 60 * 60 * 1000));
      initialEndDate = new Date(parsedForecastStartDate.getTime() + (halfWindow * 24 * 60 * 60 * 1000));
    } else {
      // Fallback: 3 months around current date
      const currentDate = new Date();
      initialStartDate = new Date(currentDate.getTime() - (90 * 24 * 60 * 60 * 1000));
      initialEndDate = new Date(currentDate.getTime() + (90 * 24 * 60 * 60 * 1000));
    }

    const categories = new Set();
    const seriesData = [];

    // Find the last actual data point for each group and series type
    const lastActualPoints = {};
    
    // First pass: collect last actual points
    timeSeriesData.forEach((point) => {
      const date = new Date(point.forecast_period || point.date);
      const isActualData = parsedForecastStartDate && date < parsedForecastStartDate;
      
      if (isActualData) {
        const groupId = point[groupingConfig.groupBy];
        if (!lastActualPoints[groupId]) {
          lastActualPoints[groupId] = {};
        }
        
        groupingConfig.seriesTypes.forEach((seriesType) => {
          const config = seriesConfig[seriesType];
          if (config && point[config.mean] !== null && point[config.mean] !== undefined) {
            if (!lastActualPoints[groupId][seriesType]) {
              lastActualPoints[groupId][seriesType] = {
                date: date.getTime(),
                value: point[config.mean]
              };
            } else if (date.getTime() > lastActualPoints[groupId][seriesType].date) {
              lastActualPoints[groupId][seriesType] = {
                date: date.getTime(),
                value: point[config.mean]
              };
            }
          }
        });
      }
    });

    // Process each data point
    timeSeriesData.forEach((point, index) => {
      const date = new Date(point.forecast_period || point.date);
      categories.add(date.getTime());
      
      // Determine if this is actual data or forecast data
      const isActualData = parsedForecastStartDate && date < parsedForecastStartDate;
      
      // Check if this group is selected
      const groupId = point[groupingConfig.groupBy];
      if (!selectedGroups.includes(groupId)) return;

      // Process each series type
      groupingConfig.seriesTypes.forEach((seriesType, typeIndex) => {
        if (!visibleSeriesTypes[seriesType]) return;

        const config = seriesConfig[seriesType];
        if (!config) return;

        // Add mean series
        if (point[config.mean] !== null && point[config.mean] !== undefined) {
          seriesData.push({
            x: date.getTime(),
            y: point[config.mean],
            series: `${config.label} ${isActualData ? 'Actual' : 'Forecast'} (${groupId})`,
            type: isActualData ? 'actual' : 'forecast',
            groupId,
            seriesType,
            metric: 'mean'
          });
        }

        // Add bounds for forecast data only
        if (!isActualData) {
          // Add lower bound
          if (visibleBounds.lower && point[config.lower] !== null && point[config.lower] !== undefined) {
            seriesData.push({
              x: date.getTime(),
              y: point[config.lower],
              series: `${config.label} Lower Bound (${groupId})`,
              type: 'lower',
              groupId,
              seriesType,
              metric: 'lower'
            });
          }
          
          // Add upper bound
          if (visibleBounds.upper && point[config.upper] !== null && point[config.upper] !== undefined) {
            seriesData.push({
              x: date.getTime(),
              y: point[config.upper],
              series: `${config.label} Upper Bound (${groupId})`,
              type: 'upper',
              groupId,
              seriesType,
              metric: 'upper'
            });
          }
        }
      });
    });

    // Add leading points for forecast series (last actual values)
    Object.entries(lastActualPoints).forEach(([groupId, seriesTypes]) => {
      if (!selectedGroups.includes(groupId)) return;
      
      Object.entries(seriesTypes).forEach(([seriesType, lastActual]) => {
        if (!visibleSeriesTypes[seriesType]) return;
        
        const config = seriesConfig[seriesType];
        if (!config) return;

        // Add leading point for forecast series
        seriesData.push({
          x: lastActual.date,
          y: lastActual.value,
          series: `${config.label} Forecast (${groupId})`,
          type: 'forecast',
          groupId,
          seriesType,
          metric: 'mean',
          isLeadingPoint: true
        });

        // Add leading points for bounds if they're visible
        if (visibleBounds.lower) {
          seriesData.push({
            x: lastActual.date,
            y: lastActual.value,
            series: `${config.label} Lower Bound (${groupId})`,
            type: 'lower',
            groupId,
            seriesType,
            metric: 'lower',
            isLeadingPoint: true
          });
        }

        if (visibleBounds.upper) {
          seriesData.push({
            x: lastActual.date,
            y: lastActual.value,
            series: `${config.label} Upper Bound (${groupId})`,
            type: 'upper',
            groupId,
            seriesType,
            metric: 'upper',
            isLeadingPoint: true
          });
        }
      });
    });

    // Group data by series
    const seriesMap = {};
    seriesData.forEach(point => {
      if (!seriesMap[point.series]) {
        seriesMap[point.series] = [];
      }
      seriesMap[point.series].push([point.x, point.y]);
    });

    // Convert to ApexCharts format
    const series = Object.entries(seriesMap).map(([name, data]) => {
      let color, dashArray;
      
      // Find the group ID from the series name or use a default
      const groupId = name.match(/\(([^)]+)\)/)?.[1] || 'default';
      const groupIndex = selectedGroups?.indexOf(groupId) || 0;
      
      // Determine series type for better color assignment
      const isActual = name.includes('Actual');
      const isForecast = name.includes('Forecast');
      const isLower = name.includes('Lower');
      const isUpper = name.includes('Upper');
      
      if (isActual) {
        color = COLORS.actual; // Black for actual data
      } else if (isForecast) {
        // Use different colors for different groups with better contrast
        const forecastIndex = groupIndex % COLORS.forecast.length;
        color = COLORS.forecast[forecastIndex];
      } else if (isLower) {
        const boundsIndex = groupIndex % COLORS.bounds.length;
        color = COLORS.bounds[boundsIndex]?.[0];
        dashArray = [4, 4];
      } else if (isUpper) {
        const boundsIndex = groupIndex % COLORS.bounds.length;
        color = COLORS.bounds[boundsIndex]?.[1];
        dashArray = [4, 4];
      } else {
        // Fallback for unknown series types
        color = COLORS.forecast[groupIndex % COLORS.forecast.length];
      }

      // Skip series with no data
      if (!data || data.length === 0) {
        return null;
      }

      return {
        name,
        data: data.sort((a, b) => a[0] - b[0]),
        color,
        dashArray,
        type: isActual ? 'line' : 'area', // Use area for forecast data, line for actual
        fill: !isActual ? {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.3,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 100]
          }
        } : undefined,
        fillOpacity: !isActual ? 0.3 : undefined
      };
    }).filter(Boolean); // Filter out null series

    return {
      series,
      categories: Array.from(categories).sort((a, b) => a - b),
      forecastStartDate: parsedForecastStartDate,
      initialStartDate,
      initialEndDate
    };
  }, [timeSeriesData, forecastStartDate, groupingConfig, seriesConfig, visibleSeriesTypes, visibleBounds, selectedGroups, initialWindowWeeks]);

  // Chart options
  const chartOptions = useMemo(() => ({
    chart: {
      type: 'line',
      height: '100%',
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: 'bold'
      }
    },
    subtitle: {
      text: subtitle,
      align: 'left',
      style: {
        fontSize: '14px',
        color: '#666'
      }
    },
    xaxis: {
      type: 'datetime',
      title: {
        text: 'Date'
      },
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM \'yy',
          day: 'dd MMM',
          hour: 'HH:mm'
        }
      },
      // Set initial zoom range
      range: undefined,
      min: chartData.initialStartDate?.getTime(),
      max: chartData.initialEndDate?.getTime()
    },
    yaxis: {
      title: {
        text: 'Quantity'
      },
      labels: {
        formatter: (value) => Math.round(value)
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      },
      y: {
        formatter: (value) => Math.round(value)
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      show: showLegend
    },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.3,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    markers: {
      size: 4,
      hover: {
        size: 6
      }
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5
      }
    },
    annotations: {
      xaxis: chartData.forecastStartDate ? [
        {
          x: chartData.forecastStartDate.getTime(),
          borderColor: '#ff6b6b',
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            borderColor: '#ff6b6b',
            style: {
              color: '#fff',
              background: '#ff6b6b'
            },
            text: 'Forecast Start'
          }
        }
      ] : []
    },
    plotOptions: {
      line: {
        curve: 'smooth'
      }
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: '100%'
          },
          legend: {
            position: 'bottom'
          }
        }
      }
    ]
  }), [title, subtitle, chartData.forecastStartDate, chartData.initialStartDate, chartData.initialEndDate, showLegend]);

  // Control handlers
  const handleSeriesTypeToggle = (seriesType) => {
    setVisibleSeriesTypes(prev => ({
      ...prev,
      [seriesType]: !prev[seriesType]
    }));
  };

  const handleGroupChange = (event) => {
    setSelectedGroups(event.target.value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Controls */}
      {showControls && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
                         {/* Series Type Toggles */}
             <Grid item>
               <Typography variant="subtitle2" sx={{ mb: 1 }}>
                 Series Types:
               </Typography>
               <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                 {groupingConfig.seriesTypes.map((seriesType) => (
                   <Chip
                     key={seriesType}
                     label={seriesConfig[seriesType]?.label || seriesType}
                     onClick={() => handleSeriesTypeToggle(seriesType)}
                     color={visibleSeriesTypes[seriesType] ? 'primary' : 'default'}
                     variant={visibleSeriesTypes[seriesType] ? 'filled' : 'outlined'}
                     size="small"
                   />
                 ))}
               </Box>
             </Grid>

             {/* Bounds Toggles */}
             <Grid item>
               <Typography variant="subtitle2" sx={{ mb: 1 }}>
                 Bounds:
               </Typography>
               <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                 <Chip
                   label="Lower Bound"
                   onClick={() => setVisibleBounds(prev => ({ ...prev, lower: !prev.lower }))}
                   color={visibleBounds.lower ? 'primary' : 'default'}
                   variant={visibleBounds.lower ? 'filled' : 'outlined'}
                   size="small"
                 />
                 <Chip
                   label="Upper Bound"
                   onClick={() => setVisibleBounds(prev => ({ ...prev, upper: !prev.upper }))}
                   color={visibleBounds.upper ? 'primary' : 'default'}
                   variant={visibleBounds.upper ? 'filled' : 'outlined'}
                   size="small"
                 />
               </Box>
             </Grid>

            {/* Group Selection */}
            <Grid item>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Groups</InputLabel>
                <Select
                  multiple
                  value={selectedGroups}
                  onChange={handleGroupChange}
                  input={<OutlinedInput label="Groups" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {availableGroups.map((group) => (
                    <MenuItem key={group} value={group}>
                      <Checkbox checked={selectedGroups.includes(group)} />
                      <ListItemText primary={group} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Chart */}
      <Box sx={{ 
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        p: 2,
        backgroundColor: '#fff',
        height,
        minHeight: '400px'
      }}>
        <ReactApexChart
          options={chartOptions}
          series={chartData.series}
          type="line"
          height="100%"
        />
      </Box>
    </Box>
  );
};

export default TimeSeriesChart; 